<script setup lang="ts">
import { storeToRefs } from 'pinia';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import Select from 'primevue/select';
import { ref } from 'vue';
import { useStorageAreaStore } from 'shared/storageAreas/stores/storageAreaStore';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { useProductionOrderStore } from 'shared/productionOrders/stores/productionOrderStore';
import type { ProductionOrder } from 'shared/productionOrders/domain/productionOrder';
import { useChangeProductionOrderApi } from '../api/changeProductionOrderApi';

defineExpose({
  show,
});

const storageAreaStore = useStorageAreaStore();
const visible = defineModel<boolean>('visible');
const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);
const productionOrderStore = useProductionOrderStore();

const selectedOrder = ref();
const orders = ref<ProductionOrder[]>([]);

const storageAreaId = ref<string>('');
const changeProductionOrderApi = useChangeProductionOrderApi();

interface ShowDialog {
  storageAreaId: string;
}

function show(options: ShowDialog) {
  const storageArea = storageAreaStore.getOrThrow(options.storageAreaId);
  storageAreaId.value = options.storageAreaId;

  orders.value = productionOrderStore.productionOrders.filter(order => order.resource?.toLocaleLowerCase() == storageArea.name.toLocaleLowerCase());
  orders.value.sort((a, b) => {
    if (a.priority != null && b.priority != null) {
      if (a.priority !== b.priority) {
        return a.priority - b.priority;
      }
    }
    if (a.plannedStartUtc != null && b.plannedStartUtc != null) {
      return new Date(a.plannedStartUtc).getTime() - new Date(b.plannedStartUtc).getTime();
    }

    return a.name.localeCompare(b.name);
  });

  selectedOrder.value = orders.value.find(order => order.name === storageArea.orderName);
  visible.value = true;
}

function clear() {
  selectedOrder.value = null;
}

async function save() {
  const storageArea = storageAreaStore.getOrThrow(storageAreaId.value);
  storageArea.orderName = selectedOrder.value ? selectedOrder.value.name : null;

  const response = await changeProductionOrderApi.execute({
    storageAreaId: storageArea.id,
    productionOrderId: selectedOrder.value ? selectedOrder.value.id : null,
  });

  if (response.ok) {
    visible.value = false;
  }
}

visible.value = false;
</script>

<template>
  <Dialog v-model:visible="visible" position="center" :modal="false" :draggable="true" :closable="true"
    :show-header="true" :header="translations.dialogs.productionOrder.editTitle">

    <Select v-model="selectedOrder" :options="orders" optionLabel="name"
      :placeholder="translations.dialogs.productionOrder.selectProductionOrder" class="w-full md:w-56"></Select>

    <Button type="button" :label="translations.generic.clear" class="ms-2" @click="clear"></Button>

    <div class="mt-2 flex justify-end gap-2">
      <Button type="button" :label="translations.generic.save" @click="save"></Button>
      <Button type="button" :label="translations.generic.cancel" severity="secondary" @click="visible = false"></Button>
    </div>
  </Dialog>
</template>
